import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/lib/Seo";
import { PageHead } from "@/pages/About";
import { PROJECTS } from "@/content/projects";
import { PATHS, ASSETS } from "@/content/site";
import Reveal from "@/components/Reveal";
import FinalCTA from "@/sections/FinalCTA";

const STATS = [
  ["+50", "منظمة خدمناها"],
  ["26", "مشروعًا موثقًا"],
  ["4000", "مستفيد أُهّل لخدمة ضيوف الرحمن"],
  ["+8", "دراسات وتقارير تخصصية"],
];

const FEATURED_SLUGS = [
  "social-investment-incubator",
  "social-innovation-lab",
  "carts-experience-index",
  "hajj-sports-services-1446",
];

const FEATURED_IMAGES = [ASSETS.meeting, ASSETS.staircase, ASSETS.heroClockTower, ASSETS.pattern];

export default function Impact() {
  const [activePath, setActivePath] = useState("all");
  const projects = PROJECTS;
  const featured = FEATURED_SLUGS.map((slug) => projects.find((p) => p.slug === slug)).filter(Boolean);
  const filtered = useMemo(
    () => activePath === "all" ? projects : projects.filter((p) => p.pathSlug === activePath),
    [activePath, projects]
  );

  return (
    <>
      <Seo path="/impact" title="أثرنا" description="مشاريع سياق ونتائجها الموثقة في تمكين المنظمات وصناعة الأثر." />
      <PageHead eyebrow="أثرنا" title="من مشاريعنا وإنجازاتنا" subtitle="نبتكر حلولنا الاستشارية وفقًا للسياق، ونمكّن المنظمات من تحقيق أثر مجتمعي مستدام" />

      <section className="bg-navy text-white border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 grid grid-cols-2 lg:grid-cols-4">
          {STATS.map(([value,label],i) => (
            <Reveal key={label} delay={i*.05} className="border-b lg:border-b-0 border-white/10 lg:border-l last:border-l-0 p-6 sm:p-9">
              <strong className="block font-display text-peach text-4xl sm:text-5xl">{value}</strong>
              <span className="block text-white/70 mt-3 leading-relaxed">{label}</span>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-paper py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
          <Reveal>
            <span className="eyebrow">من مشاريعنا</span>
            <h2 className="font-display text-navy text-3xl sm:text-4xl mt-4">مشاريع تعكس خبرتنا في ابتكار الحلول وتمكين المنظمات</h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-5 mt-10">
            {featured.map((project,i) => {
              const path = PATHS.find((p) => p.slug === project.pathSlug);
              return (
                <Reveal key={project.slug} delay={i*.08} className="h-full">
                  <Link to={`/impact/${project.slug}`} className="group relative block overflow-hidden min-h-[390px] sm:min-h-[460px]">
                    <img src={FEATURED_IMAGES[i]} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.035]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/45 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-7 sm:p-9 text-white">
                      <div className="flex items-center gap-3 text-sm text-white/75">
                        {path && <span className="text-peach">{path.title}</span>}
                      </div>
                      <h3 className="font-display text-2xl sm:text-3xl leading-snug mt-3">{project.title}</h3>
                      <p className="text-white/75 leading-relaxed mt-3 max-w-2xl">{project.excerpt}</p>
                      <span className="inline-flex items-center gap-2 mt-5 text-peach">تفاصيل المشروع <ArrowLeft size={17} className="transition-transform group-hover:-translate-x-1"/></span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section id="projects" className="bg-white py-20 sm:py-28 border-t border-navy/10 scroll-mt-20">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
          <Reveal>
            <span className="eyebrow">مشاريع سياق</span>
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5 mt-4">
              <h2 className="font-display text-navy text-3xl sm:text-4xl">جميع المشاريع</h2>
              <p className="text-blue">يعرض الآن {filtered.length} من {projects.length} مشروعًا.</p>
            </div>
          </Reveal>

          <div className="flex gap-2 overflow-x-auto py-6 mt-4" role="group" aria-label="تصفية المشاريع حسب المسار">
            <button onClick={() => setActivePath("all")} className={`shrink-0 px-5 py-3 border transition-colors ${activePath === "all" ? "bg-navy text-white border-navy" : "bg-white text-navy border-navy/20 hover:border-navy"}`}>الكل</button>
            {PATHS.map(path => (
              <button key={path.slug} onClick={() => setActivePath(path.slug)} className={`shrink-0 px-5 py-3 border transition-colors ${activePath === path.slug ? "bg-navy text-white border-navy" : "bg-white text-navy border-navy/20 hover:border-navy"}`}>{path.title}</button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-navy/10 mt-4">
            {filtered.map((project,i) => {
              const path = PATHS.find((p) => p.slug === project.pathSlug);
              return (
                <Reveal key={project.slug} delay={Math.min(i*.025,.2)}>
                  <Link to={`/impact/${project.slug}`} className="group block h-full py-8 md:px-7 border-b border-navy/10 md:border-l">
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-purple font-semibold">{path?.title}</span>
                    </div>
                    <h3 className="font-display text-navy text-xl leading-snug mt-4 group-hover:text-coral transition-colors">{project.title}</h3>
                    <p className="text-blue leading-relaxed mt-3 line-clamp-3">{project.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-coral font-medium mt-6">تفاصيل المشروع <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1"/></span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
